import { PickerObras } from '@/components/dominio/PickerObras';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCrearEnsayoEsclerometro } from '@/hooks/useEsclerometro';
import type { CrearEnsayoEsclerometroRequest } from '@adelante/shared';
import { ANGULOS_IMPACTO } from '@adelante/shared';
import { Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';

/**
 * Modal de creación de ensayo de esclerómetro. Solo crea el header — los
 * rebotes se ingresan en el detalle (uno a uno con un input rápido).
 *
 * El número de ensayo se asigna automático en el backend (consecutivo).
 */

interface Props {
  abierto: boolean;
  cerrar: () => void;
  onCreado?: (idEnsayo: number) => void;
}

const ELEMENTOS_TIPICOS = [
  'Columna',
  'Viga',
  'Losa',
  'Muro',
  'Cimiento',
  'Contrapiso',
  'Otro',
] as const;

const ETIQUETAS_ANGULO: Record<number, string> = {
  '-90': '↓ Hacia abajo (-90°)',
  '-45': '↘ Diagonal abajo (-45°)',
  '0': '→ Horizontal (0°)',
  '45': '↗ Diagonal arriba (45°)',
  '90': '↑ Hacia arriba (90°)',
};

export function ModalCrearEnsayoEsclerometro(props: Props) {
  const crear = useCrearEnsayoEsclerometro();

  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));
  const [obra, setObra] = useState<string | null>(null);
  const [idCasa, setIdCasa] = useState('');
  const [elementoSel, setElementoSel] = useState<string>('Columna');
  const [elementoLibre, setElementoLibre] = useState('');
  const [edadDias, setEdadDias] = useState<string>('');
  const [angulo, setAngulo] = useState<number>(0);
  const [equipoSerial, setEquipoSerial] = useState('');
  const [notas, setNotas] = useState('');

  useEffect(() => {
    if (props.abierto) {
      setFecha(new Date().toISOString().slice(0, 10));
      setObra(null);
      setIdCasa('');
      setElementoSel('Columna');
      setElementoLibre('');
      setEdadDias('');
      setAngulo(0);
      setEquipoSerial('');
      setNotas('');
    }
  }, [props.abierto]);

  const elementoFinal = elementoSel === 'Otro' ? elementoLibre.trim() : elementoSel;
  const edadNum = edadDias.trim() === '' ? null : Number(edadDias);
  const edadValida =
    edadNum === null || (Number.isInteger(edadNum) && edadNum > 0 && edadNum <= 3650);

  const valido = fecha.length === 10 && elementoFinal.length > 0 && edadValida;

  const onSubmit = () => {
    if (!valido) return;
    const body: CrearEnsayoEsclerometroRequest = {
      fecha,
      obra_works_no: obra,
      id_casa: idCasa.trim() || null,
      elemento_estructural: elementoFinal,
      edad_dias: edadNum,
      angulo_impacto: angulo,
      equipo_serial: equipoSerial.trim() || null,
      notas: notas.trim() || null,
    };
    crear.mutate(body, {
      onSuccess: (r) => {
        if (r.data) {
          props.cerrar();
          props.onCreado?.(r.data.id);
        }
      },
    });
  };

  return (
    <Dialog
      open={props.abierto}
      onOpenChange={(o) => {
        if (!o && !crear.isPending) props.cerrar();
      }}
    >
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nuevo ensayo de esclerómetro</DialogTitle>
          <DialogDescription>
            Ensayo no destructivo. Después de crear el header, registrarás los rebotes uno a uno en
            la pantalla de detalle.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="fecha">
                Fecha del ensayo <span className="text-destructive">*</span>
              </Label>
              <Input
                id="fecha"
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="edad">Edad del concreto (días)</Label>
              <Input
                id="edad"
                type="number"
                value={edadDias}
                onChange={(e) => setEdadDias(e.target.value)}
                placeholder="28"
                min={1}
                max={3650}
              />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label>Obra</Label>
            <PickerObras valor={obra} onChange={setObra} maxAltoPx={180} />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="id-casa">Casa / ubicación</Label>
              <Input
                id="id-casa"
                value={idCasa}
                onChange={(e) => setIdCasa(e.target.value)}
                placeholder="Casa 12, Eje C-3…"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="equipo">Equipo (serial)</Label>
              <Input
                id="equipo"
                value={equipoSerial}
                onChange={(e) => setEquipoSerial(e.target.value)}
                placeholder="Schmidt N°…"
              />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label>
              Elemento estructural <span className="text-destructive">*</span>
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {ELEMENTOS_TIPICOS.map((el) => (
                <Button
                  key={el}
                  type="button"
                  size="sm"
                  variant={elementoSel === el ? 'default' : 'outline'}
                  onClick={() => setElementoSel(el)}
                  className="h-8 px-3 text-xs"
                >
                  {el}
                </Button>
              ))}
            </div>
            {elementoSel === 'Otro' && (
              <Input
                value={elementoLibre}
                onChange={(e) => setElementoLibre(e.target.value)}
                placeholder="Especificá el elemento"
                className="mt-1"
              />
            )}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="angulo">
              Ángulo de impacto <span className="text-destructive">*</span>
            </Label>
            <Select value={String(angulo)} onValueChange={(v) => setAngulo(Number(v))}>
              <SelectTrigger id="angulo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ANGULOS_IMPACTO.map((a) => (
                  <SelectItem key={a} value={String(a)}>
                    {ETIQUETAS_ANGULO[a] ?? `${a}°`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              El ángulo afecta el valor de rebote y se usa para correcciones de tabla.
            </p>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="notas" className="text-xs text-muted-foreground">
              Notas (opcional)
            </Label>
            <textarea
              id="notas"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              className="min-h-[40px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Observaciones del ensayo…"
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={props.cerrar} disabled={crear.isPending}>
            <X className="h-4 w-4" /> Cancelar
          </Button>
          <Button
            type="button"
            onClick={onSubmit}
            disabled={!valido || crear.isPending}
            className="gap-1.5"
          >
            <Plus className="h-4 w-4" />
            {crear.isPending ? 'Creando…' : 'Crear ensayo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
